/**
 * The conversation history list.
 *
 * Everything here comes from the server and stays current without being asked
 * to: the control channel is attached for as long as the user is signed in,
 * so a reply finishing on another device moves that conversation to the top
 * of this list here too.
 */

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FiSearch } from "react-icons/fi";
import ConversationMenu from "./ConversationMenu";
import { useAssistantAbly } from "../../contexts/AIAssistantAblyContext";
import {
  loadConversations,
  removeConversation,
  renameConversation,
} from "../../state/conversationActions";
import { serverTime } from "../../utils/serverTime";
import "./ConversationHistory.scss";

const DAY = 86400000;

const startOfToday = () => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return now.getTime();
};

/**
 * Which heading a conversation sits under.
 *
 * By calendar day, not elapsed hours: something from 11pm last night belongs
 * under Yesterday at 9am, even though it is only ten hours old.
 */
const bucketOf = (iso) => {
  const stamp = serverTime(iso);
  if (Number.isNaN(stamp)) return "Older";
  const today = startOfToday();
  if (stamp >= today) return "Today";
  if (stamp >= today - DAY) return "Yesterday";
  if (stamp >= today - 7 * DAY) return "Previous 7 days";
  return "Older";
};

const BUCKET_ORDER = ["Today", "Yesterday", "Previous 7 days", "Older"];

/** The pills under the search, and which conversations each one keeps. */
const FILTERS = [
  { id: "all", label: "All", keep: () => true },
  { id: "today", label: "Today", keep: (c) => bucketOf(c.updated_at) === "Today" },
  {
    id: "week",
    label: "This week",
    keep: (c) => bucketOf(c.updated_at) !== "Older",
  },
];

/**
 * The preview as text. It is the start of the agent's reply, which is
 * Markdown, so the marks came through as "**Graduate Degree**".
 */
const plainPreview = (text) =>
  String(text).replace(/\*\*|__|`|^#+\s*/gm, "").replace(/\s+/g, " ").trim();

/** Time as the bucket wants it: fine-grained today, coarser further back. */
const timeLabel = (iso, bucket) => {
  const date = new Date(serverTime(iso));
  if (Number.isNaN(date.getTime())) return "";
  if (bucket === "Today") {
    const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes}m ago`;
    return `${Math.floor(minutes / 60)}h ago`;
  }
  const clock = date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  if (bucket === "Yesterday") return `Yesterday, ${clock}`;
  if (bucket === "Previous 7 days") {
    const days = Math.round(
      (startOfToday() - new Date(date).setHours(0, 0, 0, 0)) / DAY,
    );
    return `${days} days ago`;
  }
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short" });
};

const ConversationHistory = ({ onClose }) => {
  const dispatch = useDispatch();
  const { conversationId, openConversationById, realtimeConnectionState } =
    useAssistantAbly();
  const conversations = useSelector((state) => state?.chat?.conversations ?? []);
  const isLoading = useSelector(
    (state) => state?.chat?.conversationsLoading ?? false,
  );
  const cursor = useSelector((state) => state?.chat?.conversationsCursor ?? null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (!conversations.length) dispatch(loadConversations());
    // Only on mount. After this the control channel keeps the list current,
    // so refetching on every render would be work for nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filtered here rather than on the server: the list is already in memory
  // and a round trip per keystroke would feel worse than it reads.
  const groups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const keep = FILTERS.find((f) => f.id === filter)?.keep ?? (() => true);
    const matching = conversations.filter(
      (c) =>
        keep(c) &&
        (!needle ||
          `${c.title ?? ""} ${c.preview ?? ""}`.toLowerCase().includes(needle)),
    );

    const byBucket = new Map();
    matching.forEach((c) => {
      const bucket = bucketOf(c.updated_at);
      if (!byBucket.has(bucket)) byBucket.set(bucket, []);
      byBucket.get(bucket).push(c);
    });
    return BUCKET_ORDER.filter((b) => byBucket.has(b)).map((bucket) => ({
      bucket,
      items: byBucket.get(bucket),
    }));
  }, [conversations, query, filter]);

  // The list closes on the click; the panel shows the conversation loading
  // behind it. Waiting for the transcript first left the list sitting there
  // for as long as the request took, as if the click had not registered.
  const open = (id) => {
    onClose?.();
    openConversationById(id);
  };

  const remove = (id) => dispatch(removeConversation(id));

  // The menu owns the dialog and hands back the finished title; a native
  // prompt looked like the browser interrupting the product.
  const rename = (conversation, title) =>
    dispatch(renameConversation(conversation.id, title));

  const total = conversations.length;
  const synced =
    realtimeConnectionState === "connected"
      ? "Synced just now"
      : realtimeConnectionState === "connecting"
        ? "Syncing…"
        : "Not synced";

  return (
    <div className="lou-history">
      <div className="lou-history__search">
        <FiSearch size={17} aria-hidden />
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search conversations"
          aria-label="Search conversations"
        />
      </div>

      <div className="lou-history__filters" role="group" aria-label="Filter conversations">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            className={`lou-history__pill${filter === f.id ? " is-on" : ""}`}
            aria-pressed={filter === f.id}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="lou-history__scroll">
        {isLoading && !total ? (
          <p className="lou-history__empty">Loading…</p>
        ) : null}

        {!isLoading && !total ? (
          <p className="lou-history__empty">
            Nothing here yet. Ask Lou something and it will show up.
          </p>
        ) : null}

        {total && !groups.length ? (
          <p className="lou-history__empty">
            {query.trim()
              ? `No conversations match “${query.trim()}”.`
              : "No conversations here."}
          </p>
        ) : null}

        {groups.map(({ bucket, items }) => (
          <section key={bucket} className="lou-history__group">
            <h3 className="lou-history__heading">
              {bucket}
              <span className="lou-history__count">{items.length}</span>
            </h3>
            <ul className="lou-history__list">
              {items.map((conversation) => {
                const isActive = conversation.id === conversationId;
                const questions = conversation.message_count ?? 0;
                return (
                  <li
                    key={conversation.id}
                    className={`lou-history__item${isActive ? " is-active" : ""}`}
                  >
                    <button
                      type="button"
                      className="lou-history__row"
                      onClick={() => open(conversation.id)}
                      aria-current={isActive ? "true" : undefined}
                    >
                      <span className="lou-history__title">
                        {conversation.title || "New conversation"}
                      </span>
                      <span className="lou-history__preview">
                        {conversation.preview
                          ? plainPreview(conversation.preview)
                          : "No messages yet."}
                      </span>
                      {/* "1h ago · 6 questions" */}
                      <span className="lou-history__meta">
                        <span className="lou-history__time">
                          {timeLabel(conversation.updated_at, bucket)}
                        </span>
                        <span className="lou-history__questions">
                          {questions} question{questions === 1 ? "" : "s"}
                        </span>
                      </span>
                    </button>
                    {/* A sibling of the row, not a child: a button cannot
                        contain another button, and the menu's clicks must not
                        also open the conversation. */}
                    <ConversationMenu
                      conversation={conversation}
                      onRename={(title) => rename(conversation, title)}
                      onDelete={() => remove(conversation.id)}
                    />
                  </li>
                );
              })}
            </ul>
          </section>
        ))}

        {cursor ? (
          <button
            type="button"
            className="lou-history__more"
            onClick={() => dispatch(loadConversations({ cursor }))}
            disabled={isLoading}
          >
            {isLoading ? "Loading…" : "Show older"}
          </button>
        ) : null}
      </div>

      <div className="lou-history__footer">
        <span>
          {total} conversation{total === 1 ? "" : "s"}
        </span>
        <span>{synced}</span>
      </div>
    </div>
  );
};

export default ConversationHistory;
